import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/marketingDateUtils";
import { EmptyState } from "@/components/marketing/EmptyState";
import type { ComposerState } from "@/pages/MarketingPage";
import type { MarketingTask, NotificationItem, NotificationSource, NotificationType } from "@/types/marketing";
import {
  NOTIFICATION_SOURCE_FILTERS,
  NOTIFICATION_SOURCE_LABELS,
  NOTIFICATION_TYPE_META,
  SEVERITY_BADGE_STYLES,
} from "@/lib/marketingConstants";

/**
 * Presents filtered notifications and allows quick composer or lifecycle navigation.
 */
export function NotificationsView({
  notifications,
  loading,
  onOpenComposer,
}: {
  notifications: NotificationItem[];
  loading: boolean;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
}) {
  const [sourceFilter, setSourceFilter] = useState<NotificationSource | "all">("all");
  const filtered = useMemo(() => {
    if (sourceFilter === "all") {
      return notifications;
    }
    return notifications.filter((item) => (item.source ?? "marketing") === sourceFilter);
  }, [notifications, sourceFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {NOTIFICATION_SOURCE_FILTERS.map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={option.value === sourceFilter ? "default" : "outline"}
            className={option.value === sourceFilter ? "bg-primary text-primary-foreground" : "text-foreground/80"}
            onClick={() => setSourceFilter(option.value)}
          >
            {option.label}
          </Button>
        ))}
      </div>
      {loading ? (
        <NotificationsSkeleton count={4} />
      ) : filtered.length ? (
        filtered
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onOpenComposer={onOpenComposer}
            />
          ))
      ) : (
        <EmptyState label="No notifications yet." />
      )}
    </div>
  );
}

type NotificationCardProps = {
  notification: NotificationItem;
  onOpenComposer: (options?: Partial<ComposerState>) => void;
  onOpenLifecycle?: (taskId: string) => void;
};

function NotificationCard({ notification, onOpenComposer, onOpenLifecycle }: NotificationCardProps) {
  const meta = NOTIFICATION_TYPE_META[notification.type];
  const Icon = meta.icon;
  const source = notification.source ?? "marketing";

  return (
    <Card className="border-border/80 bg-background/30">
      <div className="flex gap-4 p-5">
        <div className="mt-1 rounded-2xl border border-border bg-background/50 p-3 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-base font-semibold text-foreground">{notification.title}</p>
              <p className="text-xs text-muted-foreground">{formatRelativeTime(notification.createdAt)}</p>
            </div>
            <Badge className={cn("capitalize", SEVERITY_BADGE_STYLES[notification.severity])}>
              {notification.severity}
            </Badge>
          </div>
          <p className="text-sm text-foreground/70">{notification.message}</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline" className="border-border/80 text-foreground/80">
              {NOTIFICATION_SOURCE_LABELS[source]}
            </Badge>
            {notification.relatedCampaignLabel ? (
              <Badge variant="outline" className="border-primary/60 text-primary">
                Campaign: {notification.relatedCampaignLabel}
              </Badge>
            ) : null}
            {notification.relatedTaskId ? (
              <Badge variant="outline" className="border-border/70 text-foreground/80">
                Task: {notification.relatedTaskId}
              </Badge>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => onOpenComposer({ initialDatetime: notification.createdAt })}>
              Open
            </Button>
            {notification.relatedTaskId ? (
              <Button size="sm" variant="outline" onClick={() => onOpenLifecycle?.(notification.relatedTaskId!)}>
                Lifecycle
              </Button>
            ) : null}
            <Button size="sm" variant="ghost">
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function NotificationsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse rounded-lg border border-border/60 bg-background/30 p-5">
          <div className="mb-2 h-4 w-1/3 rounded bg-muted" />
          <div className="mb-2 h-3 w-2/3 rounded bg-background/40" />
          <div className="h-3 w-1/2 rounded bg-background/40" />
        </div>
      ))}
    </div>
  );
}
